import { useState } from 'react'
import { ConfigProvider } from 'antd'
import svSE from 'antd/locale/sv_SE'
import { LoaderCircle } from 'lucide-react'
import { Cron } from 'react-js-cron'
import 'react-js-cron/dist/styles.css'
import { getApiErrorMessage } from '@/lib/apiError'
import { SCHEDULE_DROPDOWNS, SCHEDULE_EDITOR_THEME, SCHEDULE_PERIODS, SWEDISH_CRON_LOCALE } from '@/lib/jobSchedules'
import { describeSchedule } from '@/lib/scheduleDescription'
import { Button } from '@/components/ui/button'
import type { CronError } from 'react-js-cron'

interface JobScheduleEditorProps {
  initialCron: string
  onSave: (cron: string) => Promise<void>
  onCancel: () => void
}

const SAVE_LABEL = 'Spara schema'
const CANCEL_LABEL = 'Avbryt'
const TIME_ZONE_NOTE = 'Tiderna är svensk tid.'
const SAVE_ERROR_MESSAGE = 'Kunde inte spara schemat just nu.'

export function JobScheduleEditor({ initialCron, onSave, onCancel }: JobScheduleEditorProps): React.ReactElement {
  const [cron, setCron] = useState(initialCron)
  const [cronError, setCronError] = useState<CronError>()
  const [isSaving, setIsSaving] = useState(false)
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null)

  const hasChanged = cron !== initialCron
  const canSave = hasChanged && cronError === undefined && !isSaving

  const handleSave = async (): Promise<void> => {
    setIsSaving(true)
    setSaveErrorMessage(null)

    try {
      await onSave(cron)
    } catch (error) {
      setSaveErrorMessage(getApiErrorMessage(error, SAVE_ERROR_MESSAGE))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="space-y-3">
      <ConfigProvider
        theme={SCHEDULE_EDITOR_THEME}
        locale={svSE}
      >
        <Cron
          value={cron}
          setValue={setCron}
          onError={setCronError}
          locale={SWEDISH_CRON_LOCALE}
          allowedPeriods={SCHEDULE_PERIODS}
          dropdownsConfig={SCHEDULE_DROPDOWNS}
          clockFormat="24-hour-clock"
          clearButton={false}
          allowClear={false}
          periodicityOnDoubleClick={false}
          shortcuts={false}
          leadingZero
        />
      </ConfigProvider>

      <p className="text-sm">
        {describeSchedule(cron)}. <span className="text-muted-foreground">{TIME_ZONE_NOTE}</span>
      </p>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          className="rounded-none"
          disabled={!canSave}
          onClick={() => void handleSave()}
        >
          {isSaving && <LoaderCircle className="animate-spin" />}
          {SAVE_LABEL}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="rounded-none"
          disabled={isSaving}
          onClick={onCancel}
        >
          {CANCEL_LABEL}
        </Button>
      </div>

      {saveErrorMessage && <p className="text-xs text-destructive">{saveErrorMessage}</p>}
    </div>
  )
}
